<template>
  <header-block></header-block>
  <div class="page-container">
    <div class="inner-wide">
      <div class="card-head list-head">
        <h1>{{ $t("editor.pages") }}</h1>
        <input
          type="text"
          class="search-input"
          v-model="search"
          :placeholder="$t('editor.search_pages')"
        />
        <select class="form-control filter-select" v-model="selectedWebshop">
          <option :value="null">All webshops</option>
          <option
            v-for="webshop in webshops"
            :key="webshop.id"
            :value="webshop.id"
          >
            {{ webshop.name }}
          </option>
        </select>
        <button class="btn btn-primary" v-on:click="showAddPage">
          {{ $t("editor.add_page") }}
        </button>
      </div>

      <!-- Most pages in a region view are not that region's own: they apply
           everywhere. Say so once here rather than on every row. -->
      <div class="region-note" v-if="selectedWebshop !== null">
        Showing <strong>{{ selectedWebshopName }}</strong
        >. Pages that apply to every region are listed but locked here — switch
        to <strong>All webshops</strong> to edit them.
      </div>

      <div class="list-body">
        <aside
          class="cat-side"
          v-if="allCategories.length > 0 || uncategorisedCount > 0"
        >
          <div class="section-label">Categories</div>
          <button
            :class="{ active: categoryFilter === null }"
            v-on:click="categoryFilter = null"
          >
            All pages
          </button>
          <button
            v-for="category in allCategories"
            :key="category"
            :class="{ active: categoryFilter === category }"
            v-on:click="categoryFilter = category"
          >
            {{ category }}
          </button>
          <!-- The pages that fall through every other filter. Without this
               they can only be found by reading the whole list, which is how
               they stay uncategorised. -->
          <button
            v-if="uncategorisedCount > 0"
            class="cat-none"
            :class="{ active: categoryFilter === noCategory }"
            v-on:click="categoryFilter = noCategory"
          >
            Without category
            <span class="cat-count">{{ uncategorisedCount }}</span>
          </button>
        </aside>
        <div class="list-main">
          <!-- 1. Drafts: freshly created pages waiting for activation -->
          <div class="white-box draft-box" v-if="draftGroups.length > 0">
            <div class="card-head">
              <h1>{{ $t("editor.drafts") }}</h1>
              <span class="badge-soft badge-yellow"
                >{{ draftGroups.length }} waiting</span
              >
            </div>
            <div class="item-list">
              <template v-for="group in draftGroups" :key="group.slug">
                <PageGroupRow
                  v-if="group.variants.length > 1"
                  :group="group"
                  :expanded="!!expandedGroups[group.slug]"
                  :category="groupCategory(group)"
                  :region-label="regionLabel"
                  :variant-state="variantState"
                  v-on:toggle="toggleGroup(group.slug)"
                  v-on:filter-category="categoryFilter = $event"
                  v-on:settings="showEditPage"
                  v-on:activate="activatePage"
                  v-on:hide="hidePage"
                  v-on:restore="restorePage"
                  v-on:remove="deletePage"
                  :live-url="liveUrl"
                />
                <div
                  class="item"
                  :class="{ locked: isLocked(page) }"
                  :title="
                    isLocked(page)
                      ? 'This page applies to every region — switch the filter to All webshops to edit it'
                      : null
                  "
                  v-else
                  v-for="page in group.variants"
                  :key="page.id"
                >
                  <span class="dot" title="Draft"></span>
                  <router-link :to="'/editor/' + page.id" class="page-name">
                    {{ page.name }}
                  </router-link>
                  <button
                    class="badge-soft badge-blue category-tag"
                    v-if="categories[page.id]"
                    title="Filter by this category"
                    v-on:click="categoryFilter = categories[page.id]"
                  >
                    {{ categories[page.id] }}
                  </button>
                  <span class="shop-name" v-if="webshops.length > 1">
                    {{ webshopName(page) }}
                  </span>
                  <button
                    class="btn btn-primary btn-small"
                    v-on:click="activatePage(page)"
                  >
                    Activate
                  </button>
                  <button
                    class="icon-btn icon-btn-accent"
                    title="Page settings"
                    v-on:click="showEditPage(page)"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="currentColor"
                    >
                      <path
                        d="M2 12c0-.865.11-1.703.316-2.504A3 3 0 0 0 4.99 4.867a9.99 9.99 0 0 1 4.335-2.505 3 3 0 0 0 5.348 0 9.99 9.99 0 0 1 4.335 2.505 3 3 0 0 0 2.675 4.63c.206.8.316 1.638.316 2.503 0 .865-.11 1.703-.316 2.504a3 3 0 0 0-2.675 4.629 9.99 9.99 0 0 1-4.335 2.505 3 3 0 0 0-5.348 0 9.99 9.99 0 0 1-4.335-2.505 3 3 0 0 0-2.675-4.63C2.11 13.704 2 12.866 2 12zm4.804 3c.63 1.091.81 2.346.564 3.524.408.29.842.541 1.297.75A4.993 4.993 0 0 1 12 18c1.26 0 2.438.471 3.335 1.274.455-.209.889-.46 1.297-.75A4.993 4.993 0 0 1 17.196 15a4.993 4.993 0 0 1 2.77-2.25 8.126 8.126 0 0 0 0-1.5A4.993 4.993 0 0 1 17.195 9a4.993 4.993 0 0 1-.564-3.524 7.989 7.989 0 0 0-1.297-.75A4.993 4.993 0 0 1 12 6a4.993 4.993 0 0 1-3.335-1.274 7.99 7.99 0 0 0-1.297.75A4.993 4.993 0 0 1 6.804 9a4.993 4.993 0 0 1-2.77 2.25 8.126 8.126 0 0 0 0 1.5A4.993 4.993 0 0 1 6.805 15zM12 15a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm0-2a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
                      />
                    </svg>
                  </button>
                  <button
                    class="icon-btn icon-btn-danger"
                    title="Delete page"
                    v-on:click="deletePage(page)"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="currentColor"
                    >
                      <path
                        d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zm-9 3h2v6H9v-6zm4 0h2v6h-2v-6zM9 4v2h6V4H9z"
                      />
                    </svg>
                  </button>
                </div>
              </template>
            </div>
          </div>

          <!-- 2. Active pages -->
          <div class="white-box">
            <div class="card-head">
              <h1>{{ $t("editor.active_pages") }}</h1>
              <span class="badge-soft badge-green"
                >{{ activeGroups.length }} live</span
              >
            </div>
            <div class="item-list">
              <template v-for="group in activeGroups" :key="group.slug">
                <PageGroupRow
                  v-if="group.variants.length > 1"
                  :group="group"
                  :expanded="!!expandedGroups[group.slug]"
                  :category="groupCategory(group)"
                  :region-label="regionLabel"
                  :variant-state="variantState"
                  v-on:toggle="toggleGroup(group.slug)"
                  v-on:filter-category="categoryFilter = $event"
                  v-on:settings="showEditPage"
                  v-on:activate="activatePage"
                  v-on:hide="hidePage"
                  v-on:restore="restorePage"
                  v-on:remove="deletePage"
                  :live-url="liveUrl"
                />
                <div
                  class="item"
                  :class="{ locked: isLocked(page) }"
                  :title="
                    isLocked(page)
                      ? 'This page applies to every region — switch the filter to All webshops to edit it'
                      : null
                  "
                  v-else
                  v-for="page in group.variants"
                  :key="page.id"
                >
                  <span class="dot dot-green" title="Live"></span>
                  <router-link :to="'/editor/' + page.id" class="page-name">
                    {{ page.name }}
                  </router-link>
                  <button
                    class="badge-soft badge-blue category-tag"
                    v-if="categories[page.id]"
                    title="Filter by this category"
                    v-on:click="categoryFilter = categories[page.id]"
                  >
                    {{ categories[page.id] }}
                  </button>
                  <span class="shop-name" v-if="webshops.length > 1">
                    {{ webshopName(page) }}
                  </span>
                  <a
                    class="icon-btn icon-btn-accent"
                    :href="liveUrl(page)"
                    target="_blank"
                    title="Open live page"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="currentColor"
                    >
                      <path
                        d="M10 6v2H5v11h11v-5h2v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6zm11-3v8h-2V6.413l-7.793 7.794-1.414-1.414L17.585 5H13V3h8z"
                      />
                    </svg>
                  </a>
                  <button
                    class="icon-btn icon-btn-accent"
                    title="Page settings"
                    v-on:click="showEditPage(page)"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="currentColor"
                    >
                      <path
                        d="M2 12c0-.865.11-1.703.316-2.504A3 3 0 0 0 4.99 4.867a9.99 9.99 0 0 1 4.335-2.505 3 3 0 0 0 5.348 0 9.99 9.99 0 0 1 4.335 2.505 3 3 0 0 0 2.675 4.63c.206.8.316 1.638.316 2.503 0 .865-.11 1.703-.316 2.504a3 3 0 0 0-2.675 4.629 9.99 9.99 0 0 1-4.335 2.505 3 3 0 0 0-5.348 0 9.99 9.99 0 0 1-4.335-2.505 3 3 0 0 0-2.675-4.63C2.11 13.704 2 12.866 2 12zm4.804 3c.63 1.091.81 2.346.564 3.524.408.29.842.541 1.297.75A4.993 4.993 0 0 1 12 18c1.26 0 2.438.471 3.335 1.274.455-.209.889-.46 1.297-.75A4.993 4.993 0 0 1 17.196 15a4.993 4.993 0 0 1 2.77-2.25 8.126 8.126 0 0 0 0-1.5A4.993 4.993 0 0 1 17.195 9a4.993 4.993 0 0 1-.564-3.524 7.989 7.989 0 0 0-1.297-.75A4.993 4.993 0 0 1 12 6a4.993 4.993 0 0 1-3.335-1.274 7.99 7.99 0 0 0-1.297.75A4.993 4.993 0 0 1 6.804 9a4.993 4.993 0 0 1-2.77 2.25 8.126 8.126 0 0 0 0 1.5A4.993 4.993 0 0 1 6.805 15zM12 15a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm0-2a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
                      />
                    </svg>
                  </button>
                  <button
                    class="icon-btn"
                    title="Visible — click to hide the page"
                    v-on:click="hidePage(page)"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="currentColor"
                    >
                      <path
                        d="M12 3c5.392 0 9.878 3.88 10.819 9-.94 5.12-5.427 9-10.819 9-5.392 0-9.878-3.88-10.818-9C2.122 6.88 6.608 3 12 3zm0 16c4.235 0 7.86-2.948 8.777-7C19.86 7.948 16.235 5 12 5c-4.236 0-7.86 2.948-8.777 7C4.14 16.052 7.764 19 12 19zm0-2.5a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9zm0-2a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"
                      />
                    </svg>
                  </button>
                </div>
              </template>
              <div
                class="hint-text empty-line"
                v-if="activeGroups.length === 0"
              >
                No active pages match the current filters.
              </div>
            </div>
          </div>

          <!-- 3. Hidden pages -->
          <div class="white-box hidden-box" v-if="hiddenGroups.length > 0">
            <div class="card-head">
              <h1>{{ $t("editor.hidden_pages") }}</h1>
              <span class="badge-soft badge-grey">{{
                hiddenGroups.length
              }}</span>
            </div>
            <div class="item-list">
              <template v-for="group in hiddenGroups" :key="group.slug">
                <PageGroupRow
                  v-if="group.variants.length > 1"
                  :group="group"
                  :expanded="!!expandedGroups[group.slug]"
                  :category="groupCategory(group)"
                  :region-label="regionLabel"
                  :variant-state="variantState"
                  v-on:toggle="toggleGroup(group.slug)"
                  v-on:filter-category="categoryFilter = $event"
                  v-on:settings="showEditPage"
                  v-on:activate="activatePage"
                  v-on:hide="hidePage"
                  v-on:restore="restorePage"
                  v-on:remove="deletePage"
                  :live-url="liveUrl"
                />
                <div
                  class="item hidden-item"
                  :class="{ locked: isLocked(page) }"
                  :title="
                    isLocked(page)
                      ? 'This page applies to every region — switch the filter to All webshops to edit it'
                      : null
                  "
                  v-else
                  v-for="page in group.variants"
                  :key="page.id"
                >
                  <span class="dot dot-red" title="Hidden"></span>
                  <router-link :to="'/editor/' + page.id" class="page-name">
                    {{ page.name }}
                  </router-link>
                  <button
                    class="badge-soft badge-blue category-tag"
                    v-if="categories[page.id]"
                    title="Filter by this category"
                    v-on:click="categoryFilter = categories[page.id]"
                  >
                    {{ categories[page.id] }}
                  </button>
                  <span class="shop-name" v-if="webshops.length > 1">
                    {{ webshopName(page) }}
                  </span>
                  <button
                    class="icon-btn"
                    title="Hidden — click to restore as draft"
                    v-on:click="restorePage(page)"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="currentColor"
                    >
                      <path
                        d="M17.882 19.297A10.949 10.949 0 0 1 12 21c-5.392 0-9.878-3.88-10.819-9a10.982 10.982 0 0 1 3.34-6.066L1.392 2.808l1.415-1.415 19.799 19.8-1.415 1.414-3.31-3.31zM5.935 7.35A8.965 8.965 0 0 0 3.223 12a9.005 9.005 0 0 0 13.201 5.838l-2.028-2.028A4.5 4.5 0 0 1 8.19 9.604L5.935 7.35zm6.979 6.978l-3.242-3.242a2.5 2.5 0 0 0 3.241 3.241zm7.893 2.264l-1.431-1.43A8.935 8.935 0 0 0 20.777 12 9.005 9.005 0 0 0 9.552 5.338L7.974 3.76C9.221 3.27 10.58 3 12 3c5.392 0 9.878 3.88 10.819 9a10.947 10.947 0 0 1-2.012 4.592zm-9.084-9.084a4.5 4.5 0 0 1 4.769 4.769l-4.77-4.769z"
                      />
                    </svg>
                  </button>
                  <button
                    class="icon-btn icon-btn-danger"
                    title="Delete page"
                    v-on:click="deletePage(page)"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="currentColor"
                    >
                      <path
                        d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zm-9 3h2v6H9v-6zm4 0h2v6h-2v-6zM9 4v2h6V4H9z"
                      />
                    </svg>
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <AddPage
    v-if="addPageShow"
    v-model:show="addPageShow"
    v-model:page="selectedPage"
    v-model:webshops="webshops"
    :page-category="selectedPage ? categories[selectedPage.id] ?? '' : ''"
    :category-suggestions="allCategories"
    v-on:add="addPage"
    v-on:edit="editPage"
  />
</template>
<script>
import HeaderBlock from "../components/header";
import http from "../modules/http";
import AddPage from "../components/modals/AddPage";
import PageGroupRow from "../components/PageGroupRow";

const ARCHIVED_STORAGE_KEY = "pages-archived";
// Its own filter value, distinct from null ("no filter") and from any real
// category name — a page could legitimately be called "Without category".
const NO_CATEGORY = Symbol("no category");

export default {
  name: "PageList",
  components: { AddPage, HeaderBlock, PageGroupRow },
  data() {
    return {
      pages: [],
      webshops: [],
      selectedWebshop: null,
      addPageShow: false,
      selectedPage: null,
      search: "",
      categoryFilter: null,
      categories: {},
      archived: [],
      expandedGroups: {},
    };
  },
  computed: {
    filteredPages() {
      return this.pages
        .filter(
          (p) =>
            this.selectedWebshop === null ||
            p.webshop_id === null ||
            p.webshop_id === this.selectedWebshop
        )
        .filter((p) => {
          if (this.search.trim().length === 0) {
            return true;
          }
          const s = this.search.toLowerCase();
          return (
            p.name?.toLowerCase().includes(s) ||
            p.slug?.toLowerCase().includes(s)
          );
        })
        .filter((p) => {
          if (this.categoryFilter === null) {
            return true;
          }
          if (this.categoryFilter === this.noCategory) {
            return !this.categories[p.id];
          }
          return this.categories[p.id] === this.categoryFilter;
        });
    },
    // Region variants of the same page are separate page records — that is
    // what lets each region be built freely — but they are one page to the
    // person looking at this list. The slug ties them together: every variant
    // of Terms and conditions answers /<lang>/<region>/terms-and-conditions,
    // even the one whose name was typed differently.
    pageGroups() {
      const bySlug = {};
      for (const page of this.filteredPages) {
        const key = page.slug || "id-" + page.id;
        (bySlug[key] = bySlug[key] ?? []).push(page);
      }
      const shopOrder = (page) => {
        const i = this.webshops.findIndex((w) => w.id === page.webshop_id);
        return i < 0 ? -1 : i; // "All webshops" first, then webshop order
      };
      return Object.entries(bySlug).map(([slug, variants]) => {
        // Variants sometimes carry slightly different names ("Term And
        // Conditions"); the spelling most of them agree on wins.
        const counts = {};
        for (const v of variants) {
          counts[v.name] = (counts[v.name] ?? 0) + 1;
        }
        const name = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
        const ordered = [...variants].sort(
          (a, b) => shopOrder(a) - shopOrder(b)
        );
        return {
          slug,
          name,
          variants: ordered,
          main: ordered[0],
          activeCount: ordered.filter((v) => v.is_active).length,
          hiddenCount: ordered.filter(
            (v) => !v.is_active && this.archived.includes(v.id)
          ).length,
        };
      });
    },
    // A group lands in one section only, so a page never shows up twice:
    // live if any variant is live, otherwise hidden only if every variant is.
    draftGroups() {
      return this.pageGroups.filter(
        (g) => g.activeCount === 0 && g.hiddenCount < g.variants.length
      );
    },
    activeGroups() {
      return this.pageGroups.filter((g) => g.activeCount > 0);
    },
    hiddenGroups() {
      return this.pageGroups.filter(
        (g) => g.activeCount === 0 && g.hiddenCount === g.variants.length
      );
    },
    selectedWebshopName() {
      return (
        this.webshops.find((w) => w.id === this.selectedWebshop)?.name ?? ""
      );
    },
    // Exposed so the template can compare against it.
    noCategory() {
      return NO_CATEGORY;
    },
    // How many pages have no category at all — the number is the point: it
    // says whether this is worth opening.
    uncategorisedCount() {
      return this.pages.filter((p) => !this.categories[p.id]).length;
    },
    allCategories() {
      const present = new Set(this.pages.map((p) => p.id));
      const values = new Set();
      for (const [id, category] of Object.entries(this.categories)) {
        if (present.has(Number(id)) && category) {
          values.add(category);
        }
      }
      return [...values].sort();
    },
  },
  mounted() {
    this.loadLocalState();
    this.load();
  },
  methods: {
    loadLocalState() {
      try {
        this.archived =
          JSON.parse(localStorage.getItem(ARCHIVED_STORAGE_KEY)) ?? [];
      } catch (e) {
        this.archived = [];
      }
    },
    persistLocalState() {
      localStorage.setItem(ARCHIVED_STORAGE_KEY, JSON.stringify(this.archived));
    },
    load() {
      if (this.webshops.length === 0) {
        http.fetch("/webshops").then((data) => {
          this.webshops = data;
        });
      }
      http.fetch("/page").then((data) => {
        this.pages = data.data;
        this.categories = Object.fromEntries(
          this.pages.filter((p) => p.category).map((p) => [p.id, p.category])
        );
      });
    },
    webshopName(page) {
      if (!page.webshop_id) {
        return "All webshops";
      }
      return this.webshops.find((w) => w.id === page.webshop_id)?.name ?? "";
    },
    // Short label for the region chips on a grouped row.
    regionLabel(page) {
      return page.webshop_id ? this.webshopName(page) : "All";
    },
    // While one region is being looked at, a page that belongs to every
    // region is not that region's page: editing it here would quietly change
    // all the others too. It stays listed — the region's site does include
    // it — but read-only, and it is editable again under "All webshops".
    isLocked(page) {
      return this.selectedWebshop !== null && !page.webshop_id;
    },
    // The category of a grouped row: variants are the same page, so the first
    // category any of them carries applies to all.
    groupCategory(group) {
      for (const variant of group.variants) {
        if (this.categories[variant.id]) {
          return this.categories[variant.id];
        }
      }
      return null;
    },
    variantState(variant) {
      if (variant.is_active) {
        return "live";
      }
      return this.archived.includes(variant.id) ? "hidden" : "draft";
    },
    toggleGroup(slug) {
      this.expandedGroups[slug] = !this.expandedGroups[slug];
    },
    liveUrl(page) {
      const shop = page.webshop_id
        ? this.webshops.find((w) => w.id === page.webshop_id)
        : this.webshops.find((w) => w.is_default) ?? this.webshops[0];
      return `https://${shop?.domain}/${page.slug}`;
    },
    // The category itself is edited in the page settings modal — the list
    // only shows it; clicking the badge filters by that category.
    applyCategory(id, category) {
      if (category) {
        this.categories[id] = category;
      } else {
        delete this.categories[id];
        if (
          this.categoryFilter &&
          !this.allCategories.includes(this.categoryFilter)
        ) {
          this.categoryFilter = null;
        }
      }
    },
    activatePage(page) {
      http.fetch("/page/" + page.id + "/status", { is_active: 1 }).then(() => {
        page.is_active = true;
        this.archived = this.archived.filter((id) => id !== page.id);
        this.persistLocalState();
      });
    },
    hidePage(page) {
      http.fetch("/page/" + page.id + "/status", { is_active: 0 }).then(() => {
        page.is_active = false;
        if (!this.archived.includes(page.id)) {
          this.archived.push(page.id);
        }
        this.persistLocalState();
      });
    },
    restorePage(page) {
      this.archived = this.archived.filter((id) => id !== page.id);
      this.persistLocalState();
    },
    deletePage(page) {
      if (
        !window.confirm(
          `Delete “${page.name}” permanently? This cannot be undone.`
        )
      ) {
        return;
      }
      http
        .fetch("/page/" + page.id, {}, false, "DELETE")
        .then(() => {
          this.pages = this.pages.filter((p) => p.id !== page.id);
          delete this.categories[page.id];
          this.archived = this.archived.filter((id) => id !== page.id);
          this.persistLocalState();
        })
        .catch(() => {
          window.alert("Could not delete the page. Please try again.");
        });
    },
    showAddPage() {
      this.selectedPage = null;
      this.addPageShow = true;
    },
    addPage(page) {
      http
        .fetch("/page", page)
        .then((data) => {
          this.pages.push(data.data);
          if (page.category) this.categories[data.data.id] = page.category;
          this.addPageShow = false;
        })
        .catch((err) => {
          // Without this the rejection was swallowed and the new page just
          // never showed up. Report what the server actually objected to and
          // keep the dialog open so the entry is not lost.
          const fields = err?.errors ?? {};
          const detail = Object.values(fields).flat().join("\n");
          window.alert(
            "The page could not be created.\n\n" +
              (detail || err?.message || "Could not save the page.")
          );
        });
    },
    showEditPage(page) {
      http.fetch("/page/" + page.id).then((data) => {
        this.selectedPage = data.data;
        this.addPageShow = true;
      });
    },
    editPage(page) {
      http
        .fetch("/page/" + page.id, page)
        .then(() => {
          this.applyCategory(page.id, page.category);
          const existing = this.pages.find((p) => p.id === page.id);
          if (existing) {
            existing.name = page.name;
            existing.slug = page.slug;
          }
          this.addPageShow = false;
        })
        .catch((err) => {
          const fields = err?.errors ?? {};
          const detail = Object.values(fields).flat().join("\n");
          window.alert(
            "The changes could not be saved.\n\n" +
              (detail || err?.message || "Could not save the page.")
          );
        });
    },
  },
};
</script>
<style scoped>
/* The whole head row (title + search + webshop filter + Add page) sticks
   to the top of the scrolling page container. The container's top padding
   moves into the bar itself so nothing peeks out above it. The bar is
   frosted glass: the list shimmers through it while scrolling. */
.page-container {
  padding-top: 0;
}
.list-head {
  position: sticky;
  top: 0;
  z-index: 20;
  background: rgba(241, 245, 249, 0.65);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 24px 0 12px;
  margin: 0 0 8px;
}
.list-head h1 {
  flex: none;
  margin-right: auto;
}
.search-input {
  border: 1px solid #e2e8f0;
  border-radius: 99px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  flex: 1;
  min-width: 200px;
  max-width: 480px;
  box-sizing: border-box;
  outline-color: #7c3aed;
  background: rgba(255, 255, 255, 0.65);
}
.search-input:focus {
  background: #fff;
}

/* Category filter on the left as a sidebar */
.list-body {
  display: flex;
  gap: 18px;
  align-items: flex-start;
}
.cat-side {
  width: 168px;
  flex: none;
  position: sticky;
  top: 78px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.cat-side .section-label {
  padding: 0 12px;
  margin-bottom: 6px;
}
.cat-side button {
  border: none;
  background: transparent;
  text-align: left;
  padding: 7px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.cat-side button:hover {
  background: #fff;
}
.cat-side button.active {
  background: #7c3aed;
  color: #fff;
}
/* Set apart from the real categories: this one is a gap, not a group. */
.cat-none {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  border-top: 1px solid #e2e8f0 !important;
  border-radius: 0 0 8px 8px !important;
  padding-top: 11px !important;
  color: #92400e !important;
}
.cat-none.active {
  color: #fff !important;
}
.cat-count {
  font-size: 11px;
  font-weight: 800;
  background: #fef3c7;
  color: #92400e;
  border-radius: 999px;
  padding: 1px 7px;
}
.cat-none.active .cat-count {
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
}
.list-main {
  flex: 1;
  min-width: 0;
}
.filter-select {
  width: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 13px;
  font-weight: 600;
  background: #fff;
}
.category-filter {
  margin-bottom: 12px;
}
.draft-box {
  border: 1px dashed #c0bdbd;
}
.region-note {
  font-size: 12px;
  color: #64748b;
  background: #eef2ff;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 10px;
}
/* A page that belongs to every region stays visible in a region view — the
   region's site really does include it — but editing it here would change
   every other region too, so its controls are switched off. The live-page
   link is an <a> and stays clickable: looking is not editing. */
.item.locked {
  opacity: 0.55;
}
.item.locked .page-name,
.item.locked button {
  pointer-events: none;
}
.page-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.shop-name {
  font-size: 12px;
  color: #768692;
  font-weight: 600;
  width: 110px;
}
.category-tag {
  border: none;
  cursor: pointer;
}
.hidden-item .page-name {
  color: #768692;
}
.empty-line {
  padding: 10px;
}
</style>
